import React, { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Button, Checkbox, Flex, Space, Switch, Typography } from "antd"
import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { choose } from "choose-matching"
import classNames from "classnames"

import { balancesStore } from "@src/app/balanceStore"
import { CreateButton } from "@src/app/components/CreateButton"
import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { NoContent } from "@src/app/components/NoContent/Index"
import { paths } from "@src/app/paths"
import { BalancesCard } from "@src/pages/balances/ui/BalancesCard"
import css from "./index.module.css"

export enum SettingTab {
  All = "all",
  Bank = "bank",
  Card = "card",
  Crypto = "crypto",
  Fiat = "fiat",
}

export const BalancesPageList = observer(() => {
  const { balances, currencies, isLoading, pageSize, resetResponse, total } =
    balancesStore

  const [tab, setTab] = useState(SettingTab.All)
  const [hideZero, setHideZero] = useState<boolean>(false)

  const tabs = useMemo(() => {
    const hasCrypto =
      balances.filter(({ network }) => network !== "fiat").length > 0

    const hasFiat =
      balances.filter(({ network }) => network === "fiat").length > 0

    const hasCard =
      balances.filter(({ network }) => network === "card").length > 0

    const hasBank =
      balances.filter(({ network }) => network === "bank").length > 0

    const allIsShown =
      [hasBank, hasCard, hasFiat, hasCrypto]
        .map((f) => Number(f))
        .reduce((acc, cur) => acc + cur) > 1

    return [
      {
        isVisible: allIsShown,
        key: SettingTab.All,
        label: `All`,
      },
      {
        isVisible: hasCrypto,
        key: SettingTab.Crypto,
        label: `Crypto`,
      },
      {
        isVisible: hasFiat,
        key: SettingTab.Fiat,
        label: `Fiat`,
      },
      {
        isVisible: hasBank,
        key: SettingTab.Bank,
        label: `Bank`,
      },
      {
        isVisible: hasCard,
        key: SettingTab.Card,
        label: `Card`,
      },
    ].filter(({ isVisible }) => isVisible)
  }, [balances])

  const resolvedData = useMemo(() => {
    const byTab = {
      [SettingTab.All]: balances,
      [SettingTab.Bank]: balances.filter((b) => b.network === "bank"),
      [SettingTab.Card]: balances.filter((b) => b.network === "card"),
      [SettingTab.Crypto]: balances.filter(
        (b) =>
          b.network !== "fiat" && b.network !== "card" && b.network !== "bank",
      ),
      [SettingTab.Fiat]: balances.filter((b) => b.network === "fiat"),
    }[tab]

    if (hideZero) {
      return byTab.filter(
        (b) => Number(b.balances.available) > 0 || Number(b.balances.diff) > 0,
      )
    }

    return byTab
  }, [hideZero, tab, balances])

  useEffect(() => {
    resetResponse()
  }, [])

  return (
    <Flex className={css.wrap} gap={16} vertical>
      <Flex align="center" justify="space-between">
        <Typography.Title className={css.title}>Balances</Typography.Title>

        <Flex gap={8}>
          {tabs.map((t) => (
            <Button
              className={classNames({ [`${css.activeTab}`]: tab === t.key })}
              key={t.key}
              onClick={() => setTab(t.key)}
              size="small"
            >
              {t.label}
            </Button>
          ))}
        </Flex>

        <Flex align="center" gap={12}>
          <Typography.Text
            className={classNames([
              css.zero,
              { [`${css.zeroActive}`]: hideZero },
            ])}
          >
            Hide zero balances
          </Typography.Text>

          <Switch
            checkedChildren={<CheckOutlined />}
            defaultValue={hideZero}
            onChange={(v) => setHideZero(v)}
            unCheckedChildren={<CloseOutlined />}
          />
        </Flex>
      </Flex>
      <Flex vertical>
        {choose(
          [isLoading && !balances.length, <LoadingBlock />],
          [
            !resolvedData.length,
            <NoContent paragraph="" title="No available balances">
              <CreateButton text="Return to Projects" to={paths.default} />
            </NoContent>,
          ],
          [
            resolvedData.length > 0,
            <Flex gap={2} vertical>
              {resolvedData.map((b) => (
                <BalancesCard key={`${b.network}${b.symbol}`} wallet={b} />
              ))}
            </Flex>,
          ],
        )}
      </Flex>
    </Flex>
  )
})
